
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AddShoppingCartOutlined, AddShoppingCartRounded, DeleteOutline, SearchOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";

import { useAuth } from "../../context/AuthContext";
import { cashService, customersService, inventoryService, productsService, saleService } from "../../services";
import { emitCashSessionChanged, onCashSessionChanged } from "../../services/http/cashEvents";
import CustomSnackBar from "../../Components/CustomSnackBar";
import SalePaymentDialog from "../../Components/sale/SalePaymentDialog";
import PriceOverrideDialog from "../../Components/sale/PriceOverrideDialog";
import usePostSaleInvoiceFlow from "../../Components/invoice/usePostSaleInvoiceFlow";
import CustomerQuickCreateDialog from "../../Components/customers/CustomerQuickCreateDialog";
import { hasPermission } from "../../config/permissionUtils";
import {
  buildStockDetailPatch,
  buildProductStockMeta,
  buildPresentationOptions,
  formatStockHumanized,
  getMaxUnitsFromStockBase,
  getStockAvailabilityMeta,
  nowDateString,
  selectPreferredPresentationOption,
} from "../../Components/sale/saleStockUtils";
import { removeSalesDraft, saveSalesDraft } from "../../state/salesDraftsSlice";
import {
  buildSalesDraftPath,
  createSalesDraftId,
  getSalesDraftStorageKey,
} from "../../state/salesDraftUtils";
import { closeTab } from "../../tabs/tabsSlice";
import { normalizeTabPath } from "../../tabs/tabPath";
import MainText from "../../Components/MainText";
import ActiveGeneralInventoryAlert from "../Inventory/components/ActiveGeneralInventoryAlert";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "Q0.00";
  return `Q${parsed.toFixed(2)}`;
};

const sanitizeIntegerInput = (value) => {
  const next = String(value ?? "").replace(/[^\d]/g, "");
  if (!next) return "";
  return String(Math.max(1, parseInt(next, 10)));
};

const PHONE_MAX_LENGTH = 20;
const normalizePhoneValue = (value) => String(value || "").replace(/[^\d]/g, "").slice(0, PHONE_MAX_LENGTH);

const buildInitialSaleHeader = (branchName = "") => ({
  customer: "",
  customer_id: null,
  customer_phone: "",
  sale_date: nowDateString(),
  branch_name: branchName || "",
  notes: "",
});

const getTicketCashierName = (sale) =>
  sale?.sold_by_display ||
  sale?.created_by_name ||
  sale?.authorized_by_display ||
  "-";

const NO_OPEN_CASH_RESERVATION_MESSAGE =
  "No hay caja abierta. Puedes consultar productos, pero no puedes vender.";
const GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE =
  "Inventario general activo. Puedes consultar productos, pero las ventas de esta sucursal estan suspendidas.";
const SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE =
  "Producto en conteo activo. Puedes consultar precio y formatos, pero no venderlo hasta terminar el conteo.";

const normalizePendingPosCartItem = (cartItem) => {
  const fallbackId =
    cartItem?.tempId ||
    cartItem?.id ||
    `pos-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const qtyPresentation = Math.max(
    1,
    parseInt(cartItem?.qty_presentation ?? cartItem?.qty ?? 1, 10)
  );
  const qtyInBase = Number(cartItem?.qty_in_base || 1);
  const unitPrice = Number(cartItem?.unit_price || 0);
  const costPrice = Number(cartItem?.unit_cost_snapshot ?? cartItem?.cost_price ?? 0);
  const basePrice = Number(cartItem?.base_price ?? unitPrice);

  return {
    ...cartItem,
    id: fallbackId,
    tempId: fallbackId,
    product_id: cartItem?.product_id || null,
    product_presentation_id: cartItem?.product_presentation_id || null,
    product_description: cartItem?.product_description || "Producto",
    presentation_name: cartItem?.presentation_name || cartItem?.format_name || "Presentacion",
    qty_presentation: qtyPresentation,
    qty_base: qtyPresentation * qtyInBase,
    qty_in_base: qtyInBase,
    unit_price: unitPrice,
    unit_cost_snapshot: costPrice,
    cost_price: costPrice,
    base_price: basePrice,
    line_total: Number(cartItem?.line_total ?? qtyPresentation * unitPrice),
    _fromPOS: true,
  };
};

function SalesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const saleWasCompleted = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const salesPath = normalizeTabPath(`${location.pathname}${location.search}`);
  const { isSuperuser, effectivePerms = [], currentBranch } = useAuth();

  const branchId = currentBranch?.id || null;
  const salesDraftKey = useMemo(() => getSalesDraftStorageKey(location.search), [location.search]);
  const savedDraft = useSelector((state) => state.salesDrafts.drafts[salesDraftKey] || null);
  const canManageSales = hasPermission(effectivePerms, "sale_create", isSuperuser);
  const canPayNow = hasPermission(effectivePerms, "sale_pay_now", isSuperuser);
  const canOverridePrice = hasPermission(effectivePerms, "sale_discount_override", isSuperuser);

  const [saleHeader, setSaleHeader] = useState(() => buildInitialSaleHeader(currentBranch?.name));
  const [sale, setSale] = useState(null);
  const [pendingPosCartItems, setPendingPosCartItems] = useState([]);
  const [cashInfo, setCashInfo] = useState(null);
  const [salesConfig, setSalesConfig] = useState({ allow_cashier_queue: true });
  const [stockCountBlock, setStockCountBlock] = useState({
    mode: null,
    countId: null,
    blockedPresentationIds: {},
    blockedProductIds: {},
  });
  const hasOpenCashSession = Boolean(cashInfo?.session && cashInfo.session.status === "OPEN");
  const cashierQueueEnabled = salesConfig?.allow_cashier_queue !== false;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchStockByProduct, setSearchStockByProduct] = useState({});
  const [pinSearchResults, setPinSearchResults] = useState(false);
  const [presentationOptionsById, setPresentationOptionsById] = useState({});
  const [stockByPresentation, setStockByPresentation] = useState({});
  const [stockDetailByPresentation, setStockDetailByPresentation] = useState({});
  const stockByPresentationRef = useRef({});
  const stockDetailByPresentationRef = useRef({});
  const productDetailByIdRef = useRef({});
  const productOptionsByIdRef = useRef({});
  const restoredDraftKeyRef = useRef(null);
  const posCartHydratedByDraftRef = useRef({});
  const [hydratedDraftKey, setHydratedDraftKey] = useState(null);

  const [qtyDraftByItem, setQtyDraftByItem] = useState({});
  const [priceDraftByItem, setPriceDraftByItem] = useState({});
  const [lastValidPriceByItem, setLastValidPriceByItem] = useState({});
  const [editingPriceItemId, setEditingPriceItemId] = useState(null);
  const [itemUpdating, setItemUpdating] = useState(false);

  const [sendSubmitting, setSendSubmitting] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payFallbackToPendingOnClose, setPayFallbackToPendingOnClose] = useState(false);
  const [transferAccounts, setTransferAccounts] = useState([]);
  const [loadingTransferAccounts, setLoadingTransferAccounts] = useState(false);
  const [priceOverrideDialog, setPriceOverrideDialog] = useState({
    open: false,
    item: null,
    newUnitPrice: null,
    basePrice: null,
    costPrice: null,
  });

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerLookupStatus, setCustomerLookupStatus] = useState("idle");
  const [customerCreateDialogOpen, setCustomerCreateDialogOpen] = useState(false);

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);
  const { handlePostSaleInvoiceFlow, invoiceDialog } = usePostSaleInvoiceFlow({ showSnack });

  useEffect(() => {
    if (!location.search) {
      navigate("/sales/history", { replace: true });
    }
  }, [location.search, navigate]);

  const handleCustomerSelected = useCallback((customer, successMessage = "") => {
    const customerId = Number(customer?.id);
    const customerName = String(customer?.name || "").trim();
    const customerPhone = normalizePhoneValue(customer?.phone_number || "");
    setSaleHeader((prev) => ({
      ...prev,
      customer: customerName,
      customer_id: Number.isInteger(customerId) && customerId > 0 ? customerId : null,
      customer_phone: customerPhone,
    }));
    setCustomerLookupStatus("found");
    setCustomerCreateDialogOpen(false);
    if (successMessage) {
      showSnack(successMessage, "success");
    }
  }, [showSnack]);

  const handleCustomerPhoneChange = useCallback((event) => {
    const rawPhone = event?.target?.value ?? "";
    const normalizedPhone = normalizePhoneValue(rawPhone);
    setSaleHeader((prev) => {
      const currentNormalizedPhone = normalizePhoneValue(prev.customer_phone);
      const keepCurrentCustomer =
        normalizedPhone &&
        currentNormalizedPhone &&
        normalizedPhone === currentNormalizedPhone &&
        prev.customer_id;
        return {
          ...prev,
          customer_phone: normalizedPhone,
          customer_id: keepCurrentCustomer ? prev.customer_id : null,
          customer: keepCurrentCustomer ? prev.customer : "",
        };
    });
    setCustomerLookupStatus(normalizedPhone ? "typing" : "idle");
  }, []);

  const customerPhoneQuery = useMemo(
    () => normalizePhoneValue(saleHeader.customer_phone),
    [saleHeader.customer_phone]
  );

  const customerDisplayValue = useMemo(() => {
    return String(saleHeader.customer || "").trim() || "CF";
  }, [saleHeader.customer]);

  const customerLookupHelperText = useMemo(() => {
    if (customerLookupLoading) return "Buscando cliente por telefono...";
    if (customerLookupStatus === "found") return "Cliente encontrado y asociado automaticamente.";
    if (customerPhoneQuery && customerLookupStatus === "not_found") {
      return "No existe cliente con ese telefono. Si no lo creas, la venta seguira como CF.";
    }
    return "Sin telefono la venta se registra como CF.";
  }, [customerLookupLoading, customerLookupStatus, customerPhoneQuery]);

  const saleItems = useMemo(() => toArray(sale?.items), [sale?.items]);
  const hasPendingPosCart = !sale?.id && pendingPosCartItems.length > 0;
  const visibleSaleItems = hasPendingPosCart ? pendingPosCartItems : saleItems;
  const pendingPosCartTotal = useMemo(
    () => pendingPosCartItems.reduce((sum, item) => sum + Number(item?.line_total || 0), 0),
    [pendingPosCartItems]
  );
  const addedProductIds = useMemo(
    () =>
      new Set(
        visibleSaleItems
          .map((item) =>
            String(
              item?.product_id ||
                presentationOptionsById[String(item?.product_presentation_id || "")]?.[0]?.product_id ||
                ""
            )
          )
          .filter(Boolean)
      ),
    [presentationOptionsById, visibleSaleItems]
  );
  const saleSubtotal = hasPendingPosCart ? pendingPosCartTotal : Number(sale?.subtotal || 0);
  const saleDiscountTotal = hasPendingPosCart ? 0 : Number(sale?.discount_total || 0);
  const saleTaxTotal = hasPendingPosCart ? 0 : Number(sale?.tax_total || 0);
  const saleTotal = hasPendingPosCart ? pendingPosCartTotal : Number(sale?.total || 0);

  useEffect(() => {
    stockByPresentationRef.current = stockByPresentation;
  }, [stockByPresentation]);

  useEffect(() => {
    stockDetailByPresentationRef.current = stockDetailByPresentation;
  }, [stockDetailByPresentation]);

  const mergeStockByPresentation = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setStockByPresentation((prev) => {
      const next = { ...prev, ...patch };
      stockByPresentationRef.current = next;
      return next;
    });
  }, []);

  const mergeStockDetailByPresentation = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setStockDetailByPresentation((prev) => {
      const next = { ...prev, ...patch };
      stockDetailByPresentationRef.current = next;
      return next;
    });
  }, []);

  const registerPresentationOptions = useCallback((productId, options) => {
    if (!Array.isArray(options) || options.length === 0) return;
    if (productId != null) {
      productOptionsByIdRef.current[String(productId)] = options;
    }
    setPresentationOptionsById((prev) => {
      const next = { ...prev };
      options.forEach((option) => {
        next[String(option.id)] = options;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    setLastValidPriceByItem((prev) => {
      const next = {};
      saleItems.forEach((row) => {
        const rowId = String(row.id);
        const existing = prev[rowId];
        if (existing !== undefined) {
          next[rowId] = existing;
          return;
        }
        const currentPrice = Number(row?.unit_price || 0);
        next[rowId] = Number.isFinite(currentPrice) ? currentPrice.toFixed(2) : "0.00";
      });
      return next;
    });
  }, [saleItems]);

  const loadCurrentCash = useCallback(async () => {
    if (!branchId) {
      setCashInfo(null);
      return;
    }
    try {
      const data = await cashService.currentSession({ branch_id: branchId });
      setCashInfo(data || null);
    } catch {
      setCashInfo(null);
    }
  }, [branchId]);

  const loadSalesConfig = useCallback(async () => {
    if (!branchId) {
      setSalesConfig({ allow_cashier_queue: true });
      return;
    }
    try {
      const data = await saleService.getSalesConfig({ branch_id: branchId });
      setSalesConfig({
        branch_id: data?.branch_id || branchId,
        allow_cashier_queue: data?.allow_cashier_queue !== false,
      });
    } catch {
      setSalesConfig({ branch_id: branchId, allow_cashier_queue: true });
    }
  }, [branchId]);

  const loadActiveStockCountBlock = useCallback(async () => {
    if (!branchId) {
      setStockCountBlock({
        mode: null,
        countId: null,
        blockedPresentationIds: {},
        blockedProductIds: {},
      });
      return;
    }

    try {
      const counts = toArray(
        await inventoryService.listStockCounts({
          branch_id: branchId,
          status: "DRAFT",
        })
      );
      const activeCount = counts[0] || null;
      if (!activeCount) {
        setStockCountBlock({
          mode: null,
          countId: null,
          blockedPresentationIds: {},
          blockedProductIds: {},
        });
        return;
      }

      const countType = String(activeCount?.count_type || "").toUpperCase();
      if (countType === "GENERAL") {
        setStockCountBlock({
          mode: "GENERAL",
          countId: activeCount.id,
          blockedPresentationIds: {},
          blockedProductIds: {},
        });
        return;
      }

      const detail = await inventoryService.getStockCount(activeCount.id, { branch_id: branchId });
      const blockedPresentationIds = {};
      const blockedProductIds = {};
      toArray(detail?.items).forEach((item) => {
        const presentationId = Number(item?.product_presentation?.id);
        const productId = Number(item?.product_presentation?.product?.id);
        if (Number.isInteger(presentationId) && presentationId > 0) {
          blockedPresentationIds[String(presentationId)] = true;
        }
        if (Number.isInteger(productId) && productId > 0) {
          blockedProductIds[String(productId)] = true;
        }
      });

      setStockCountBlock({
        mode: "SELECTIVE",
        countId: activeCount.id,
        blockedPresentationIds,
        blockedProductIds,
      });
    } catch {
      setStockCountBlock({
        mode: null,
        countId: null,
        blockedPresentationIds: {},
        blockedProductIds: {},
      });
    }
  }, [branchId]);

  const loadTransferAccounts = useCallback(async () => {
    if (!branchId) {
      setTransferAccounts([]);
      return;
    }
    setLoadingTransferAccounts(true);
    try {
      const data = await saleService.listTransferAccounts({ branch_id: branchId, active: true });
      setTransferAccounts(toArray(data));
    } catch {
      setTransferAccounts([]);
    } finally {
      setLoadingTransferAccounts(false);
    }
  }, [branchId]);

  useEffect(() => {
    setSaleHeader((prev) => ({ ...prev, branch_name: currentBranch?.name || "" }));
  }, [currentBranch?.name]);

  useEffect(() => {
    if (!customerPhoneQuery) {
      setCustomerLookupLoading(false);
      setCustomerLookupStatus("idle");
      return undefined;
    }

    let cancelled = false;
    const timeout = setTimeout(async () => {
      setCustomerLookupLoading(true);
      try {
        const customer = await customersService.findByPhone(customerPhoneQuery);
        if (cancelled) return;
        setSaleHeader((prev) => {
          if (normalizePhoneValue(prev.customer_phone) !== customerPhoneQuery) {
            return prev;
          }
          return {
            ...prev,
              customer: String(customer?.name || "").trim(),
              customer_id: Number(customer?.id) || null,
              customer_phone: normalizePhoneValue(customer?.phone_number || customerPhoneQuery),
            };
        });
        setCustomerLookupStatus("found");
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 404) {
          setSaleHeader((prev) => {
            if (normalizePhoneValue(prev.customer_phone) !== customerPhoneQuery) {
              return prev;
            }
            return {
              ...prev,
              customer: "",
              customer_id: null,
            };
          });
          setCustomerLookupStatus("not_found");
          return;
        }
        setCustomerLookupStatus("idle");
      } finally {
        if (!cancelled) {
          setCustomerLookupLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [customerPhoneQuery]);

  useEffect(() => {
    if (restoredDraftKeyRef.current === salesDraftKey) {
      return;
    }

    const incomingPosCartItems = Array.isArray(savedDraft?.cartItemsFromPOS)
      ? savedDraft.cartItemsFromPOS
      : [];
    const nextHeader = {
      ...buildInitialSaleHeader(currentBranch?.name),
      ...(savedDraft?.saleHeader || {}),
      branch_name: currentBranch?.name || "",
    };

    setSaleHeader(nextHeader);
    setSale(savedDraft?.sale || null);
    if (incomingPosCartItems.length > 0) {
      setPendingPosCartItems(incomingPosCartItems.map(normalizePendingPosCartItem));
      if (!posCartHydratedByDraftRef.current[salesDraftKey]) {
        posCartHydratedByDraftRef.current[salesDraftKey] = true;
        const { updatedAt, ...draftWithoutRuntimeFields } = savedDraft || {};
        dispatch(
          saveSalesDraft({
            key: salesDraftKey,
            draft: {
              ...draftWithoutRuntimeFields,
              cartItemsFromPOS: [],
            },
          })
        );
      }
    } else if (!posCartHydratedByDraftRef.current[salesDraftKey]) {
      setPendingPosCartItems([]);
      posCartHydratedByDraftRef.current[salesDraftKey] = true;
    }
    setSearchTerm(savedDraft?.searchTerm || "");
    setSearchResults([]);
    setSearchStockByProduct({});
    setPinSearchResults(Boolean(savedDraft?.pinSearchResults));
    setQtyDraftByItem(savedDraft?.qtyDraftByItem || {});
    setPriceDraftByItem(savedDraft?.priceDraftByItem || {});
    setLastValidPriceByItem(savedDraft?.lastValidPriceByItem || {});
    setEditingPriceItemId(null);
    setPresentationOptionsById({});
    setStockByPresentation({});
    productDetailByIdRef.current = {};
    productOptionsByIdRef.current = {};
    restoredDraftKeyRef.current = salesDraftKey;
    setHydratedDraftKey(salesDraftKey);
  }, [currentBranch?.name, dispatch, salesDraftKey, savedDraft]);

  useEffect(() => {
    if (saleWasCompleted.current) return;
    if (hydratedDraftKey !== salesDraftKey) return undefined;
    const timeout = setTimeout(() => {
      dispatch(
        saveSalesDraft({
          key: salesDraftKey,
          draft: {
            saleHeader,
            sale,
            cartItemsFromPOS: pendingPosCartItems,
            searchTerm,
            pinSearchResults,
            qtyDraftByItem,
            priceDraftByItem,
            lastValidPriceByItem,
          },
        })
      );
    }, 250);

    return () => clearTimeout(timeout);
  }, [
    dispatch,
    hydratedDraftKey,
    lastValidPriceByItem,
    pendingPosCartItems,
    pinSearchResults,
    priceDraftByItem,
    qtyDraftByItem,
    sale,
    saleHeader,
    salesDraftKey,
    searchTerm,
  ]);

  const getProductCountBlockMeta = useCallback(
    (product) => {
      if (stockCountBlock.mode === "GENERAL") {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const productId = String(product?.id || "");
      if (productId && stockCountBlock.blockedProductIds[productId]) {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const options =
        productOptionsByIdRef.current[productId] ||
        buildPresentationOptions(product);
      const hasBlockedPresentation = options.some(
        (option) => stockCountBlock.blockedPresentationIds[String(option?.id || "")]
      );
      if (hasBlockedPresentation) {
        return {
          blocked: true,
          label: "Productos en conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      return { blocked: false, label: "", detail: "" };
    },
    [stockCountBlock]
  );

  const getItemCountBlockMeta = useCallback(
    (item) => {
      if (stockCountBlock.mode === "GENERAL") {
        return {
          blocked: true,
          label: "En conteo",
          detail: GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      const presentationId = String(item?.product_presentation_id || "");
      const productId = String(item?.product_id || "");
      if (
        (presentationId && stockCountBlock.blockedPresentationIds[presentationId]) ||
        (productId && stockCountBlock.blockedProductIds[productId])
      ) {
        return {
          blocked: true,
          label: "En conteo",
          detail: SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        };
      }

      return { blocked: false, label: "", detail: "" };
    },
    [stockCountBlock]
  );

  const hasBlockedSaleItems = useMemo(
    () => visibleSaleItems.some((item) => getItemCountBlockMeta(item).blocked),
    [getItemCountBlockMeta, visibleSaleItems]
  );

  const submitting = sendSubmitting || paySubmitting || itemUpdating || !hasOpenCashSession;
  const primarySaleActionLabel = cashierQueueEnabled
    ? hasPendingPosCart
      ? "Confirmar y enviar a caja"
      : "Enviar a Caja"
    : "Cobrar";

  const completeSaleSubmission = useCallback(
    (message = "Venta enviada a caja", severity = "success") => {
      setSubmitted(true);
      saleWasCompleted.current = true;
      dispatch(removeSalesDraft(salesDraftKey));
      dispatch(closeTab(salesPath));
      showSnack(message, severity);
      navigate(cashierQueueEnabled ? "/cashbox" : "/sales/history", { replace: true });
    },
    [cashierQueueEnabled, dispatch, navigate, salesDraftKey, salesPath, showSnack]
  );

  useEffect(() => {
    loadCurrentCash();
  }, [loadCurrentCash]);

  useEffect(() => {
    loadSalesConfig();
  }, [loadSalesConfig]);

  useEffect(() => {
    loadTransferAccounts();
  }, [loadTransferAccounts]);

  useEffect(() => {
    loadActiveStockCountBlock();
  }, [loadActiveStockCountBlock]);

  useEffect(() => {
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (!branchId || (payloadBranchId && payloadBranchId !== branchId)) return;
      loadCurrentCash();
    });
    return unsubscribe;
  }, [branchId, loadCurrentCash]);

  useEffect(() => {
    const query = searchTerm.trim();
    if (!branchId || query.length < 2) {
      setSearchResults([]);
      setSearchStockByProduct({});
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list(
          {
            search: query,
            active: "true",
            sales_search: "true",
            branch_id: branchId,
            limit: 20,
          },
          { signal: controller.signal }
        );
        if (!cancelled) {
          const results = toArray(data);
          const allPresentationIds = [];
          const optionsByProductId = {};
          results.forEach((product) => {
            const options = buildPresentationOptions(product);
            optionsByProductId[String(product.id)] = options;
            options.forEach((option) => {
              if (Number.isInteger(Number(option?.id)) && Number(option.id) > 0) {
                allPresentationIds.push(Number(option.id));
              }
            });
          });

          const stockRows = allPresentationIds.length
            ? toArray(
                await inventoryService.listStocks({
                  branch_id: branchId,
                  product_presentation_ids: Array.from(new Set(allPresentationIds)).join(","),
                  exclude_sale_id: sale?.id || undefined,
                })
              )
            : [];

          const availableByPresentation = {};
          stockRows.forEach((row) => {
            availableByPresentation[String(row?.product_presentation_id)] = Number(
              row?.qty_available ?? row?.qty_base ?? 0
            );
          });

          const stockDetailPatch = buildStockDetailPatch(stockRows);
          const nextSearchStockByProduct = {};
          const nextStockByPresentation = {};
          results.forEach((product) => {
            const options = optionsByProductId[String(product.id)] || [];
            registerPresentationOptions(product.id, options);
            options.forEach((option) => {
              const availableQty = Number(
                availableByPresentation[String(option.id)] ?? option?.stock_qty_base ?? 0
              );
              availableByPresentation[String(option.id)] = availableQty;
              nextStockByPresentation[String(option.id)] = availableQty;
            });
            nextSearchStockByProduct[String(product.id)] = buildProductStockMeta(
              options,
              availableByPresentation,
              stockDetailPatch
            );
          });
          mergeStockByPresentation(nextStockByPresentation);
          mergeStockDetailByPresentation(stockDetailPatch);
          setSearchResults(results);
          setSearchStockByProduct(nextSearchStockByProduct);
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }
        if (!cancelled) {
          setSearchResults([]);
          setSearchStockByProduct({});
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [branchId, mergeStockByPresentation, mergeStockDetailByPresentation, registerPresentationOptions, sale?.id, searchTerm]);

  useEffect(() => {
    setStockByPresentation({});
    stockByPresentationRef.current = {};
    setStockDetailByPresentation({});
    stockDetailByPresentationRef.current = {};
  }, [branchId]);

  const startSaleIfNeeded = useCallback(async () => {
    if (!branchId) return null;
    if (sale?.id) return sale;
    if (!hasOpenCashSession) {
      showSnack(NO_OPEN_CASH_RESERVATION_MESSAGE, "warning");
      return null;
    }
    try {
      const started = await saleService.startSale({
        branch_id: branchId,
        comment: (saleHeader.notes || "").trim(),
      });
      setSale(started || null);
      return started || null;
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo iniciar la venta.";
      showSnack(detail, "error");
      return null;
    }
  }, [branchId, hasOpenCashSession, sale, saleHeader.notes, showSnack]);

  const loadPresentationOptionsForProduct = useCallback(
    async (productId) => {
      const key = String(productId || "");
      const cachedOptions = productOptionsByIdRef.current[key];
      if (Array.isArray(cachedOptions) && cachedOptions.length > 0) {
        return cachedOptions;
      }

      let detail = productDetailByIdRef.current[key];
      if (!detail) {
        detail = searchResults.find((row) => String(row?.id) === key) || null;
      }
      if (!detail) {
        detail = await productsService.get(productId);
      }
      if (detail) {
        productDetailByIdRef.current[key] = detail;
      }

      const options = buildPresentationOptions(detail);
      registerPresentationOptions(productId, options);
      return options;
    },
    [registerPresentationOptions, searchResults]
  );

  const ensureStockForPresentationIds = useCallback(
    async (presentationIds = [], options = {}) => {
      if (!branchId) return {};
      const uniqueIds = Array.from(
        new Set(
          presentationIds
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value) && value > 0)
        )
      );
      if (uniqueIds.length === 0) return {};

      const forceRefresh = Boolean(options?.forceRefresh);
      const excludeSaleId = options?.excludeSaleId ?? sale?.id;
      const current = stockByPresentationRef.current || {};
      const idsToFetch = forceRefresh
        ? uniqueIds
        : uniqueIds.filter((id) => current[String(id)] === undefined);
      if (idsToFetch.length > 0) {
        const rows = toArray(
          await inventoryService.listStocks({
            branch_id: branchId,
            product_presentation_ids: idsToFetch.join(","),
            exclude_sale_id: excludeSaleId || undefined,
          })
        );
        const fetched = {};
        rows.forEach((row) => {
          fetched[String(row?.product_presentation_id)] = Number(row?.qty_available ?? row?.qty_base ?? 0);
        });
        idsToFetch.forEach((id) => {
          const key = String(id);
          if (fetched[key] === undefined) {
            fetched[key] = 0;
          }
        });
        mergeStockByPresentation(fetched);
        mergeStockDetailByPresentation(buildStockDetailPatch(rows));
      }

      const latest = stockByPresentationRef.current || {};
      return uniqueIds.reduce((accumulator, id) => {
        accumulator[String(id)] = Number(latest[String(id)] || 0);
        return accumulator;
      }, {});
    },
    [branchId, mergeStockByPresentation, mergeStockDetailByPresentation, sale?.id]
  );

  const getItemOptions = useCallback(
    (item) => {
      const key = String(item?.product_presentation_id || "");
      const mapped = presentationOptionsById[key];
      if (Array.isArray(mapped) && mapped.length > 0) return mapped;

      const fallbackOption = {
        id: item?.product_presentation_id,
        product_id: item?.product_id || null,
        name: item?.presentation_name || "Presentacion",
        qty_in_base: Number(item?.qty_base || 0) / Math.max(Number(item?.qty_presentation || 1), 1),
        cost_price: Number(item?.unit_cost_snapshot || 0),
        base_sale_price: Number(item?.unit_price || 0),
      };
      return fallbackOption.id ? [fallbackOption] : [];
    },
    [presentationOptionsById]
  );

  const getItemPricingContext = useCallback(
    (item) => {
      const options = getItemOptions(item);
      const selected = options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
      const costPrice = Number(selected?.cost_price ?? item?.unit_cost_snapshot ?? 0);
      const basePrice = Number(selected?.base_sale_price ?? item?.unit_price ?? costPrice);
      return {
        options,
        costPrice: Number.isFinite(costPrice) ? costPrice : 0,
        basePrice: Number.isFinite(basePrice) ? basePrice : 0,
      };
    },
    [getItemOptions]
  );

  const getQtyInBaseForItem = useCallback(
    (item) => {
      const options = getItemOptions(item);
      const selected = options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
      const fallback = Number(item?.qty_base || 0) / Math.max(Number(item?.qty_presentation || 1), 1);
      const qtyInBase = Number(selected?.qty_in_base ?? fallback);
      if (!Number.isFinite(qtyInBase) || qtyInBase <= 0) return 1;
      return qtyInBase;
    },
    [getItemOptions]
  );

  const fetchStockForPresentation = useCallback(
    async (presentationId, options = {}) => {
      const stockMap = await ensureStockForPresentationIds([presentationId], {
        forceRefresh: true,
        excludeSaleId: options?.excludeSaleId,
      });
      return Number(stockMap[String(presentationId)] || 0);
    },
    [ensureStockForPresentationIds]
  );

  useEffect(() => {
    if (!hasPendingPosCart || pendingPosCartItems.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      for (const cartItem of pendingPosCartItems) {
        if (cancelled) return;
        if (cartItem.product_id) {
          await loadPresentationOptionsForProduct(cartItem.product_id);
        }
        if (cancelled) return;
        if (cartItem.product_presentation_id) {
          await fetchStockForPresentation(cartItem.product_presentation_id);
        }
      }
    })().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    fetchStockForPresentation,
    hasPendingPosCart,
    loadPresentationOptionsForProduct,
    pendingPosCartItems,
  ]);

  const getStockChipState = useCallback(
    (item) => {
      const key = String(item?.product_presentation_id || "");
      const externalAvailableBase = Number(stockByPresentation[key] || 0);
      const qtyInBase = getQtyInBaseForItem(item);
      const rowId = String(item?.id || "");
      const draftQty = qtyDraftByItem[rowId];
      const currentQty = draftQty !== undefined ? Number(draftQty) : Number(item?.qty_presentation || 0);
      const ownReservedBase =
        Number.isFinite(currentQty) && currentQty > 0 && Number.isFinite(qtyInBase) && qtyInBase > 0
          ? currentQty * qtyInBase
          : Number(item?.qty_base || 0);
      const remainingBase = Math.max(0, externalAvailableBase - ownReservedBase);
      return getStockAvailabilityMeta(remainingBase, qtyInBase, 1);
    },
    [getQtyInBaseForItem, qtyDraftByItem, stockByPresentation]
  );

  const formatTooltipStockValue = useCallback(
    (value, item) => {
      const options = getItemOptions(item);
      const safeValue = Math.max(0, Number(value || 0));
      const formatted = formatStockHumanized(safeValue, options);
      return formatted === "Sin stock" ? "0" : formatted;
    },
    [getItemOptions]
  );

  const getStockTooltipContent = useCallback(
    (item, countBlockMeta = null) => {
      const key = String(item?.product_presentation_id || "");
      const detail = stockDetailByPresentation[key];
      const qtyAvailableExternal = Number(detail?.qty_available ?? stockByPresentation[key] ?? 0);
      const qtyReservedSoft = Number(detail?.qty_reserved_soft ?? 0);
      const qtyReservedHard = Number(detail?.qty_reserved_hard ?? 0);
      const qtyReservedOthers = qtyReservedSoft + qtyReservedHard;
      const qtyInBase = getQtyInBaseForItem(item);
      const rowId = String(item?.id || "");
      const draftQty = qtyDraftByItem[rowId];
      const currentQty = draftQty !== undefined ? Number(draftQty) : Number(item?.qty_presentation || 0);
      const qtyReservedOwn =
        Number.isFinite(currentQty) && currentQty > 0 && Number.isFinite(qtyInBase) && qtyInBase > 0
          ? currentQty * qtyInBase
          : Number(item?.qty_base || 0);
      const qtyAvailable = Math.max(0, qtyAvailableExternal - qtyReservedOwn);
      const qtyInventory = Number(detail?.qty_base ?? qtyAvailableExternal + qtyReservedOthers);

      return (
        <Stack spacing={0.45} sx={{ minWidth: 220, py: 0.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {item?.product_description || "Stock"}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.78) }}>
            {item?.presentation_name || "Presentacion actual"}
          </Typography>
          {countBlockMeta?.blocked ? (
            <>
              <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.16), my: 0.35 }} />
              <Typography variant="caption" sx={{ color: theme.palette.warning.light, fontWeight: 700 }}>
                {countBlockMeta.label}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.82) }}>
                {countBlockMeta.detail}
              </Typography>
            </>
          ) : null}
          <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.16), my: 0.35 }} />
          <Stack direction="row" justifyContent="space-between" spacing={1.2}>
            <Typography variant="caption">En inventario</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatTooltipStockValue(qtyInventory, item)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={1.2}>
            <Typography variant="caption">Comprometido</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatTooltipStockValue(qtyReservedOthers + qtyReservedOwn, item)}
            </Typography>
          </Stack>
          {(qtyReservedSoft > 0 || qtyReservedHard > 0) && (
            <Stack direction="row" justifyContent="space-between" spacing={1.2}>
              <Typography variant="caption">Reservado por otros</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {`${formatTooltipStockValue(qtyReservedSoft, item)} / ${formatTooltipStockValue(qtyReservedHard, item)}`}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" spacing={1.2}>
            <Typography variant="caption">Reservado en esta venta</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatTooltipStockValue(qtyReservedOwn, item)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={1.2}>
            <Typography variant="caption">Disponible restante</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.success.light }}>
              {formatTooltipStockValue(qtyAvailable, item)}
            </Typography>
          </Stack>
        </Stack>
      );
    },
    [
      formatTooltipStockValue,
      getQtyInBaseForItem,
      qtyDraftByItem,
      stockByPresentation,
      stockDetailByPresentation,
      theme.palette.common.white,
      theme.palette.success.light,
      theme.palette.warning.light,
    ]
  );

  const getCachedMaxUnitsForItem = useCallback(
    (item) => {
      const stockBase = stockByPresentation[String(item?.product_presentation_id || "")];
      if (stockBase === undefined) return null;
      const qtyInBase = getQtyInBaseForItem(item);
      if (!Number.isFinite(qtyInBase) || qtyInBase <= 0) return null;
      return Math.max(0, Math.floor(Number(stockBase || 0) / qtyInBase));
    },
    [getQtyInBaseForItem, stockByPresentation]
  );

  const getMaxUnitsForOption = useCallback(
    async (option, item = null) => {
      const presentationId = Number(option?.id || option?.product_presentation_id);
      const qtyInBase = Number(option?.qty_in_base || 0);
      if (!Number.isInteger(presentationId) || presentationId <= 0 || !Number.isFinite(qtyInBase) || qtyInBase <= 0) {
        return 0;
      }
      const stockMap = await ensureStockForPresentationIds([presentationId]);
      return getMaxUnitsFromStockBase(stockMap[String(presentationId)], qtyInBase);
    },
    [ensureStockForPresentationIds]
  );

  const stockLineErrors = useMemo(() => {
    const errors = {};
    visibleSaleItems.forEach((item) => {
      const rowId = String(item?.id || "");
      if (!rowId) return;
      const availableUnits = getCachedMaxUnitsForItem(item);
      if (!Number.isInteger(availableUnits)) return;
      const requestedUnits = Math.max(0, Number(item?.qty_presentation || 0));
      if (requestedUnits > availableUnits) {
        errors[rowId] = {
          available: availableUnits,
          requested: requestedUnits,
          message: `Disponible: ${availableUnits}. No puedes vender ${requestedUnits}.`,
        };
      }
    });
    return errors;
  }, [getCachedMaxUnitsForItem, visibleSaleItems]);
  const hasStockLineErrors = Object.keys(stockLineErrors).length > 0;
  const primarySaleActionDisabled =
    submitting ||
    submitted ||
    hasBlockedSaleItems ||
    (hasPendingPosCart && hasStockLineErrors) ||
    (!cashierQueueEnabled && !canPayNow);

  useEffect(() => {
    if (!branchId) return;
    const ids = Array.from(
      new Set(
        saleItems
          .map((item) => Number(item?.product_presentation_id))
          .filter((id) => Number.isInteger(id) && id > 0)
      )
    );
    const missing = ids.filter((id) => stockByPresentation[String(id)] === undefined);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const presentationId of missing) {
        if (cancelled) return;
        await fetchStockForPresentation(presentationId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, fetchStockForPresentation, saleItems, stockByPresentation]);

  const handleAddProduct = useCallback(
    async (product) => {
      if (!product?.id || !branchId) return;
      const countBlockMeta = getProductCountBlockMeta(product);
      if (countBlockMeta.blocked) {
        showSnack(countBlockMeta.detail, "warning");
        return;
      }
      if (!hasOpenCashSession) {
        showSnack(NO_OPEN_CASH_RESERVATION_MESSAGE, "warning");
        return;
      }
      if (hasPendingPosCart) {
        showSnack("Confirma primero el carrito trasladado desde POS.", "info");
        return;
      }

      setItemUpdating(true);
      try {
        const options = await loadPresentationOptionsForProduct(product.id);
        if (options.length === 0) {
          showSnack("El producto no tiene presentaciones activas con precio.", "warning");
          return;
        }
        const stockBaseMap = await ensureStockForPresentationIds(options.map((option) => option.id));
        const preferred = selectPreferredPresentationOption(options, stockBaseMap);
        const selectedOption = preferred?.option || options[0];
        const maxUnits = preferred?.maxUnits ?? 0;
        if (!selectedOption || maxUnits <= 0) {
          const stockMeta = buildProductStockMeta(options, stockBaseMap, stockDetailByPresentationRef.current);
          showSnack(
            stockMeta?.status === "reserved"
              ? `${product.description || "Producto"} en reserva. Disponible: 0.`
              : `Sin stock disponible para ${product.description || "este producto"}.`,
            "warning"
          );
          return;
        }

        const currentSale = await startSaleIfNeeded();
        if (!currentSale?.id) return;

        const updated = await saleService.addItem(currentSale.id, {
          branch_id: branchId,
          product_presentation_id: selectedOption.id,
          qty_presentation: String(Math.min(1, maxUnits)),
        });
        const nextSale = updated || currentSale;
        setSale(nextSale);
        await fetchStockForPresentation(selectedOption.id, { excludeSaleId: nextSale?.id || currentSale.id });
        if (!pinSearchResults) {
          setSearchTerm("");
          setSearchResults([]);
        }
      } catch (error) {
        const detail = error?.response?.data?.detail || "No se pudo agregar el producto.";
        showSnack(detail, "error");
      } finally {
        setItemUpdating(false);
      }
    },
    [
      branchId,
      ensureStockForPresentationIds,
      fetchStockForPresentation,
      hasOpenCashSession,
      hasPendingPosCart,
      loadPresentationOptionsForProduct,
      pinSearchResults,
      getProductCountBlockMeta,
      showSnack,
      startSaleIfNeeded,
    ]
  );

  const handleRemoveItem = async (item) => {
    if (!item?.id || !branchId) return;
    if (item?._fromPOS) {
      const key = String(item.id);
      setPendingPosCartItems((prev) => prev.filter((row) => String(row.id) !== key));
      setQtyDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setLastValidPriceByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (editingPriceItemId === key) {
        setEditingPriceItemId(null);
      }
      return;
    }
    if (!sale?.id) return;
    setItemUpdating(true);
    try {
      const updated = await saleService.updateItem(sale.id, item.id, {
        branch_id: branchId,
        remove: true,
      });
      if (updated?.deleted) {
        dispatch(removeSalesDraft(salesDraftKey));
        setSale(null);
        setQtyDraftByItem({});
        setPriceDraftByItem({});
        setLastValidPriceByItem({});
        setEditingPriceItemId(null);
        setSubmitted(false);
        saleWasCompleted.current = false;
        showSnack("Venta eliminada al quedar sin productos", "info");
        return;
      }
      setSale(updated || sale);
      await fetchStockForPresentation(item.product_presentation_id);
    } catch {
      showSnack("No se pudo eliminar el item.", "error");
    } finally {
      setItemUpdating(false);
    }
  };

  const commitQtyChange = async (item) => {
    if (!item?.id || !branchId) return;
    const key = String(item.id);
    const raw = qtyDraftByItem[key];
    if (raw === undefined) return;
    const parsed = Number(raw);
    const currentQty = Number(item?.qty_presentation || 1);

    if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
      setQtyDraftByItem((prev) => {
        const next = { ...prev, [key]: String(Math.max(1, currentQty)) };
        return next;
      });
      showSnack("Cantidad invalida. Debe ser un entero mayor a 0.", "warning");
      return;
    }

    const options = getItemOptions(item);
    const selected =
      options.find((row) => String(row.id) === String(item?.product_presentation_id)) || options[0];
    const maxUnits = await getMaxUnitsForOption(selected, item);
    if (maxUnits <= 0) {
      showSnack(`Sin stock disponible para ${item?.product_description || "este producto"}.`, "warning");
      setQtyDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    const safeQty = Math.min(parsed, maxUnits);
    if (safeQty < parsed) {
      showSnack(`Cantidad ajustada a stock disponible: ${safeQty}.`, "warning");
    }

    if (item?._fromPOS) {
      const qtyInBase = Number(selected?.qty_in_base ?? getQtyInBaseForItem(item) ?? 1);
      setPendingPosCartItems((prev) =>
        prev.map((row) =>
          String(row.id) === key
            ? normalizePendingPosCartItem({
                ...row,
                qty_presentation: safeQty,
                qty: safeQty,
                qty_in_base: qtyInBase,
                qty_base: safeQty * qtyInBase,
                line_total: Number(safeQty) * Number(row?.unit_price || 0),
              })
            : row
        )
      );
      setQtyDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    if (!sale?.id) return;
    setItemUpdating(true);
    try {
      const updated = await saleService.updateItem(sale.id, item.id, {
        branch_id: branchId,
        qty_presentation: safeQty,
      });
      setSale(updated || sale);
      await fetchStockForPresentation(item.product_presentation_id);
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo actualizar la cantidad.";
      showSnack(detail, "error");
    } finally {
      setItemUpdating(false);
      setQtyDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handlePresentationChange = async (item, nextPresentationId) => {
    if (!item?.id || !branchId) return;
    if (!nextPresentationId || String(nextPresentationId) === String(item.product_presentation_id)) return;

    const options = getItemOptions(item);
    const nextOption = options.find((row) => String(row.id) === String(nextPresentationId));
    if (!nextOption) {
      showSnack(`Sin stock disponible para ${item?.product_description || "esta presentacion"}.`, "warning");
      return;
    }
    const externalStockMap = await ensureStockForPresentationIds([nextPresentationId], {
      forceRefresh: true,
    });
    const externalStockBase = Number(externalStockMap[String(nextPresentationId)] || 0);
    const newQtyInBase = Number(nextOption?.qty_in_base || 1);
    const maxUnits =
      Number.isFinite(newQtyInBase) && newQtyInBase > 0
        ? Math.floor(externalStockBase / newQtyInBase)
        : 0;
    if (maxUnits <= 0) {
      showSnack("Sin stock disponible para este formato. Disponible: 0.", "warning");
      return;
    }
    const currentQty = Math.max(1, parseInt(item?.qty_presentation || 1, 10));
    const safeQty = Math.min(currentQty, maxUnits);
    if (safeQty < currentQty) {
      showSnack(`Cantidad ajustada a ${safeQty} (maximo disponible en este formato).`, "warning");
    }

    if (item?._fromPOS) {
      setPendingPosCartItems((prev) => {
        const mergeTarget = prev.find(
          (row) =>
            String(row.id) !== String(item.id) &&
            String(row.product_presentation_id) === String(nextPresentationId)
        );
        if (mergeTarget) {
          const mergedQty = Math.min(
            Number(mergeTarget.qty_presentation || 0) + safeQty,
            maxUnits
          );
          return prev
            .filter((row) => String(row.id) !== String(item.id))
            .map((row) =>
              String(row.id) === String(mergeTarget.id)
                ? normalizePendingPosCartItem({
                    ...row,
                    product_presentation_id: nextOption.id,
                    presentation_name: nextOption.name || "Presentacion",
                    qty_in_base: Number(nextOption.qty_in_base || 1),
                    qty_presentation: mergedQty,
                    qty: mergedQty,
                    qty_base: mergedQty * Number(nextOption.qty_in_base || 1),
                    unit_price: Number(nextOption.base_sale_price || row.unit_price || 0),
                    unit_cost_snapshot: Number(nextOption.cost_price || row.unit_cost_snapshot || 0),
                    cost_price: Number(nextOption.cost_price || row.cost_price || 0),
                    base_price: Number(nextOption.base_sale_price || row.base_price || 0),
                    line_total: mergedQty * Number(nextOption.base_sale_price || row.unit_price || 0),
                  })
                : row
            );
        }

        return prev.map((row) =>
          String(row.id) === String(item.id)
            ? normalizePendingPosCartItem({
                ...row,
                product_presentation_id: nextOption.id,
                presentation_name: nextOption.name || "Presentacion",
                qty_in_base: Number(nextOption.qty_in_base || 1),
                qty_presentation: safeQty,
                qty: safeQty,
                qty_base: safeQty * Number(nextOption.qty_in_base || 1),
                unit_price: Number(nextOption.base_sale_price || row.unit_price || 0),
                unit_cost_snapshot: Number(nextOption.cost_price || row.unit_cost_snapshot || 0),
                cost_price: Number(nextOption.cost_price || row.cost_price || 0),
                base_price: Number(nextOption.base_sale_price || row.base_price || 0),
                line_total: safeQty * Number(nextOption.base_sale_price || row.unit_price || 0),
              })
            : row
        );
      });
      setEditingPriceItemId(null);
      return;
    }

    if (!sale?.id) return;
    setItemUpdating(true);
    try {
      const updated = await saleService.updateItem(sale.id, item.id, {
        branch_id: branchId,
        product_presentation_id: nextPresentationId,
        qty_presentation: safeQty,
      });
      setSale(updated || sale);
      setEditingPriceItemId(null);
      await fetchStockForPresentation(item.product_presentation_id);
      await fetchStockForPresentation(nextPresentationId);
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo cambiar la presentacion.";
      showSnack(detail, "error");
    } finally {
      setItemUpdating(false);
    }
  };

  const handlePriceDraftChange = (item, rawValue) => {
    const itemId = String(item.id);
    setPriceDraftByItem((prev) => ({
      ...prev,
      [itemId]: rawValue,
    }));
  };

  const handleStartPriceEdit = (item) => {
    const key = String(item.id);
    const current = Number(item?.unit_price || 0).toFixed(2);
    setPriceDraftByItem((prev) => ({
      ...prev,
      [key]: prev[key] ?? current,
    }));
    setEditingPriceItemId(key);
  };

  const commitPriceChange = useCallback(async (item) => {
    if (!item?.id || !branchId) return;
    const key = String(item.id);
    const raw = priceDraftByItem[key];
    if (raw === undefined) return "noop";

    const { costPrice, basePrice } = getItemPricingContext(item);
    const fallback = Number(lastValidPriceByItem[key] ?? item.unit_price ?? basePrice);
    const fallbackNormalized = Number.isFinite(fallback)
      ? Number(fallback).toFixed(2)
      : Number(basePrice).toFixed(2);
    const normalizedRaw = String(raw ?? "").trim();
    const parsed = Number(normalizedRaw);

    if (!normalizedRaw || !Number.isFinite(parsed)) {
      setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
      setEditingPriceItemId(null);
      return "restored";
    }
    if (parsed < costPrice) {
      showSnack("No puede vender por debajo del costo", "warning");
      setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
      setEditingPriceItemId(null);
      return "blocked";
    }
    if (parsed < basePrice && !canOverridePrice) {
      showSnack("No autorizado para aplicar descuento", "warning");
      setPriceDraftByItem((prev) => ({ ...prev, [key]: fallbackNormalized }));
      setEditingPriceItemId(null);
      return "blocked";
    }

    const normalized = Number(parsed).toFixed(2);
    if (normalized === Number(item?.unit_price || 0).toFixed(2)) {
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditingPriceItemId(null);
      return "unchanged";
    }
    if (Number(parsed) < Number(basePrice) && canOverridePrice) {
      setPriceOverrideDialog({
        open: true,
        item,
        newUnitPrice: Number(parsed),
        basePrice: Number(basePrice),
        costPrice: Number(costPrice),
      });
      setEditingPriceItemId(null);
      return "override";
    }

    if (item?._fromPOS) {
      setPendingPosCartItems((prev) =>
        prev.map((row) =>
          String(row.id) === key
            ? normalizePendingPosCartItem({
                ...row,
                unit_price: Number(normalized),
                line_total: Number(normalized) * Number(row?.qty_presentation || 0),
              })
            : row
        )
      );
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditingPriceItemId(null);
      return "applied";
    }

    if (!sale?.id) return "noop";
    setItemUpdating(true);
    try {
      const updated = await saleService.updateItem(sale.id, item.id, {
        branch_id: branchId,
        unit_price: normalized,
      });
      setSale(updated || sale);
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      return "applied";
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo actualizar el precio.";
      showSnack(detail, "error");
      setPriceDraftByItem((prev) => ({
        ...prev,
        [key]: fallbackNormalized,
      }));
      return "error";
    } finally {
      setItemUpdating(false);
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEditingPriceItemId(null);
    }
  }, [
    branchId,
    canOverridePrice,
    getItemPricingContext,
    lastValidPriceByItem,
    priceDraftByItem,
    sale,
    showSnack,
  ]);

  const handleConfirmPriceOverride = useCallback(async (overridePrice) => {
    const item = priceOverrideDialog.item;
    if (!item?.id || !branchId) {
      setPriceOverrideDialog((prev) => ({ ...prev, open: false }));
      return;
    }

    const key = String(item.id);
    const { costPrice } = getItemPricingContext(item);
    const parsed = Number(overridePrice);
    if (!Number.isFinite(parsed) || parsed < costPrice) {
      showSnack("No puede vender por debajo del costo", "warning");
      return;
    }

    const normalized = parsed.toFixed(2);
    if (item?._fromPOS) {
      setPendingPosCartItems((prev) =>
        prev.map((row) =>
          String(row.id) === key
            ? normalizePendingPosCartItem({
                ...row,
                unit_price: Number(normalized),
                line_total: Number(normalized) * Number(row?.qty_presentation || 0),
              })
            : row
        )
      );
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setPriceOverrideDialog({
        open: false,
        item: null,
        newUnitPrice: null,
        basePrice: null,
        costPrice: null,
      });
      setEditingPriceItemId(null);
      showSnack("Precio actualizado con override autorizado.", "success");
      return;
    }

    if (!sale?.id) {
      setPriceOverrideDialog((prev) => ({ ...prev, open: false }));
      return;
    }
    setItemUpdating(true);
    try {
      const updated = await saleService.updateItem(sale.id, item.id, {
        branch_id: branchId,
        unit_price: normalized,
      });
      setSale(updated || sale);
      setLastValidPriceByItem((prev) => ({ ...prev, [key]: normalized }));
      setPriceDraftByItem((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      showSnack("Precio actualizado con override autorizado.", "success");
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo aplicar el override de precio.";
      showSnack(detail, "error");
    } finally {
      setItemUpdating(false);
      setPriceOverrideDialog({
        open: false,
        item: null,
        newUnitPrice: null,
        basePrice: null,
        costPrice: null,
      });
      setEditingPriceItemId(null);
    }
  }, [branchId, getItemPricingContext, priceOverrideDialog.item, sale, showSnack]);

  const validateDraftPricesBeforeSend = useCallback(() => {
    if (editingPriceItemId) {
      const editingItem = saleItems.find((row) => String(row.id) === String(editingPriceItemId));
      showSnack(
        `Confirma el precio de ${editingItem?.product_description || "un producto"} antes de enviar.`,
        "warning"
      );
      return false;
    }

    for (const item of saleItems) {
      const key = String(item.id);
      const draft = priceDraftByItem[key];
      if (draft === undefined) continue;
      const normalizedRaw = String(draft ?? "").trim();
      const parsed = Number(normalizedRaw);
      if (!normalizedRaw || !Number.isFinite(parsed)) {
        showSnack(`Precio pendiente para ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      const { costPrice } = getItemPricingContext(item);
      if (parsed < costPrice) {
        showSnack(`Precio no puede ser menor al costo en ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      if (Number(parsed).toFixed(2) !== Number(item?.unit_price || 0).toFixed(2)) {
        showSnack(`Confirma el precio editado de ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
    }
    return true;
  }, [editingPriceItemId, getItemPricingContext, priceDraftByItem, saleItems, showSnack]);

  const buildPaymentRows = useCallback((paymentPayload, totalValue) => {
    const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
    const referenceParts = [paymentPayload?.reference || ""];
    if (paymentPayload?.bank) {
      referenceParts.push(`Banco: ${paymentPayload.bank}`);
    }
    if (paymentPayload?.payment_date) {
      referenceParts.push(`Fecha: ${paymentPayload.payment_date}`);
    }
    return [
      {
        payment_method: method,
        amount: Number(totalValue || 0).toFixed(2),
        cash_received: method === "CASH" ? paymentPayload?.cash_received || Number(totalValue || 0).toFixed(2) : undefined,
        reference: referenceParts.filter(Boolean).join(" | "),
        account_id: method === "TRANSFER" ? paymentPayload?.account_id : undefined,
      },
    ];
  }, []);

  const confirmTransferredCart = useCallback(async (targetStatus = "PENDING", paymentPayload = null) => {
    if (!branchId) return false;
    if (pendingPosCartItems.length === 0) {
      showSnack("No hay productos pendientes por confirmar.", "warning");
      return false;
    }
    if (!hasOpenCashSession) {
      showSnack("Debes abrir caja antes de enviar a Caja.", "warning");
      return false;
    }
    if (targetStatus === "PENDING" && !cashierQueueEnabled) {
      showSnack("La bandeja de caja de cobro no esta habilitada para esta sucursal.", "warning");
      return false;
    }
    const presentationIds = Array.from(
      new Set(
        pendingPosCartItems
          .map((item) => Number(item?.product_presentation_id))
          .filter((id) => Number.isInteger(id) && id > 0)
      )
    );
    if (presentationIds.length > 0) {
      const liveStockMap = await ensureStockForPresentationIds(presentationIds, { forceRefresh: true });
      for (const item of pendingPosCartItems) {
        const presentationId = Number(item?.product_presentation_id);
        const qtyInBase = getQtyInBaseForItem(item);
        if (!Number.isInteger(presentationId) || presentationId <= 0 || !Number.isFinite(qtyInBase) || qtyInBase <= 0) {
          continue;
        }
        const availableUnits = getMaxUnitsFromStockBase(liveStockMap[String(presentationId)], qtyInBase);
        const requestedUnits = Math.max(0, Number(item?.qty_presentation || 0));
        if (requestedUnits > availableUnits) {
          showSnack(`Disponible: ${availableUnits}. No puedes vender ${requestedUnits}.`, "warning");
          return false;
        }
      }
    }
    if (hasStockLineErrors) {
      const firstError = Object.values(stockLineErrors)[0];
      showSnack(firstError?.message || "Hay lineas con stock insuficiente.", "warning");
      return false;
    }

    if (editingPriceItemId) {
      const editingItem = pendingPosCartItems.find((row) => String(row.id) === String(editingPriceItemId));
      if (editingItem) {
        const commitResult = await commitPriceChange(editingItem);
        if (commitResult === "override" || commitResult === "blocked" || commitResult === "error") {
          return false;
        }
      }
    }

    for (const item of pendingPosCartItems) {
      const key = String(item.id);
      const draft = priceDraftByItem[key];
      if (draft === undefined) continue;
      const normalizedRaw = String(draft ?? "").trim();
      const parsed = Number(normalizedRaw);
      if (!normalizedRaw || !Number.isFinite(parsed)) {
        showSnack(`Precio pendiente para ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      const { costPrice } = getItemPricingContext(item);
      if (parsed < costPrice) {
        showSnack(`Precio no puede ser menor al costo en ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
      if (Number(parsed).toFixed(2) !== Number(item?.unit_price || 0).toFixed(2)) {
        showSnack(`Confirma el precio editado de ${item?.product_description || "un producto"}.`, "warning");
        return false;
      }
    }

    const invalidItem = pendingPosCartItems.find((item) => {
      const qty = Number(item?.qty_presentation);
      return !item?.product_presentation_id || !Number.isInteger(qty) || qty <= 0;
    });
    if (invalidItem) {
      showSnack("Revisa el carrito trasladado antes de confirmar.", "warning");
      return false;
    }

    if (targetStatus === "IN_CASHIER") {
      setPaySubmitting(true);
    } else {
      setSendSubmitting(true);
    }
    try {
      const confirmedSale = await saleService.confirmCart({
        branch_id: branchId,
        customer_id: saleHeader.customer_id ?? null,
        notes: String(saleHeader.notes || "").trim() || undefined,
        items: pendingPosCartItems.map((item) => ({
          product_presentation_id: item.product_presentation_id,
          qty_presentation: Number(item.qty_presentation || 0),
          unit_price: Number(item.unit_price || 0).toFixed(2),
        })),
        target_status: targetStatus,
      });
      if (targetStatus === "IN_CASHIER") {
        const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
        const shouldAutoPrint = method === "CASH" || method === "CARD" || method === "VOUCHER";
        let paidSale = null;
        try {
          paidSale = await saleService.paySale(confirmedSale.id, {
            branch_id: branchId,
            customer_id: saleHeader.customer_id ?? null,
            payments: buildPaymentRows(paymentPayload, pendingPosCartTotal),
          });
        } catch (paymentError) {
          await saleService.deleteSale(confirmedSale.id, branchId, { force: true }).catch(() => {});
          throw paymentError;
        }
        setPendingPosCartItems([]);
        setSale(paidSale || confirmedSale || null);
        setPayFallbackToPendingOnClose(false);
        setPayDialogOpen(false);
        emitCashSessionChanged({ branchId, action: "sale_paid", saleId: paidSale?.id || confirmedSale?.id });
        if (String(paidSale?.status || "").toUpperCase() === "COMPLETED" && shouldAutoPrint) {
          await handlePostSaleInvoiceFlow({
            sale: paidSale,
            branchName: paidSale?.branch_name || currentBranch?.name || saleHeader.branch_name || "-",
            cashierName: getTicketCashierName(paidSale),
            allowTicketPrint: shouldAutoPrint,
          });
        }
        completeSaleSubmission(
          String(paidSale?.status || "").toUpperCase() === "WAITING_PAYMENT"
            ? "Transferencia registrada. Venta en espera de aprobacion."
            : "Venta cobrada correctamente.",
          String(paidSale?.status || "").toUpperCase() === "WAITING_PAYMENT" ? "info" : "success"
        );
        return true;
      }
      setPendingPosCartItems([]);
      setSale(confirmedSale || null);
      emitCashSessionChanged({ branchId, action: "sale_pending", saleId: confirmedSale?.id });
      completeSaleSubmission("Venta enviada a caja", "success");
      return true;
    } catch (error) {
      const shortages = toArray(error?.response?.data?.shortages);
      if (shortages.length > 0) {
        const first = shortages[0] || {};
        const available = first?.available ?? first?.available_qty_base ?? "0";
        const requested = first?.requested ?? first?.needed_qty_base ?? "?";
        showSnack(`Disponible: ${available}. No puedes vender ${requested}.`, "error");
      } else {
        const detail =
          error?.response?.data?.detail ||
          "No se pudo confirmar el carrito trasladado a venta normal.";
        showSnack(detail, "error");
      }
      return false;
    } finally {
      if (targetStatus === "IN_CASHIER") {
        setPaySubmitting(false);
      } else {
        setSendSubmitting(false);
      }
    }
  }, [
    branchId,
    buildPaymentRows,
    cashierQueueEnabled,
    commitPriceChange,
    completeSaleSubmission,
    currentBranch?.name,
    editingPriceItemId,
    ensureStockForPresentationIds,
    getQtyInBaseForItem,
    getItemPricingContext,
    hasStockLineErrors,
    hasOpenCashSession,
    handlePostSaleInvoiceFlow,
    pendingPosCartItems,
    pendingPosCartTotal,
    priceDraftByItem,
    saleHeader.customer_id,
    saleHeader.branch_name,
    saleHeader.notes,
    stockLineErrors,
    showSnack,
  ]);

  const sendSaleToCash = useCallback(async () => {
    if (!sale?.id || !branchId) return false;
    if (!cashierQueueEnabled) {
      showSnack("La bandeja de caja de cobro no esta habilitada para esta sucursal.", "warning");
      return false;
    }
    if (!hasOpenCashSession) {
      showSnack("Debes abrir caja antes de enviar a Caja.", "warning");
      return false;
    }
    setSendSubmitting(true);
    try {
      const updated = await saleService.sendToCash(sale.id, {
        branch_id: branchId,
        customer_id: saleHeader.customer_id ?? null,
        status: "PENDING",
      });
      setSale(updated || sale);
      emitCashSessionChanged({ branchId, action: "sale_pending", saleId: sale.id });
      completeSaleSubmission();
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo enviar la venta a Caja.";
      showSnack(detail, "error");
      return false;
    } finally {
      setSendSubmitting(false);
    }
  }, [branchId, cashierQueueEnabled, completeSaleSubmission, hasOpenCashSession, sale, saleHeader.customer_id, showSnack]);

  const handleOpenPayDialog = (fallbackToPending = true) => {
    if (!sale?.id && !hasPendingPosCart) return;
    if (visibleSaleItems.length === 0) {
      showSnack("No puedes enviar una venta sin items.", "warning");
      return;
    }
    if (!canPayNow) {
      showSnack('Esta sucursal no usa bandeja de cobro. Asigna el permiso "Cobrar al instante" para poder completar ventas.', "warning");
      return;
    }
    setPayFallbackToPendingOnClose(Boolean(fallbackToPending && cashierQueueEnabled && sale?.id));
    setPayDialogOpen(true);
  };

  const handleSendToCashClick = async () => {
    if (hasBlockedSaleItems) {
      showSnack(
        stockCountBlock.mode === "GENERAL"
          ? GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE
          : SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        "warning"
      );
      return;
    }
    if (hasPendingPosCart) {
      if (!hasOpenCashSession) {
        showSnack(cashierQueueEnabled ? "Debes abrir caja antes de enviar a Caja." : "Debes abrir caja antes de cobrar.", "warning");
        return;
      }
      if (!cashierQueueEnabled) {
        handleOpenPayDialog(false);
        return;
      }
      await confirmTransferredCart();
      return;
    }
    if (!sale?.id || !branchId) {
      showSnack("Agrega al menos un producto para crear la venta.", "warning");
      return;
    }
    if (saleItems.length === 0) {
      showSnack("No puedes enviar una venta sin items.", "warning");
      return;
    }
    if (!hasOpenCashSession) {
      showSnack(cashierQueueEnabled ? "Debes abrir caja antes de enviar a Caja." : "Debes abrir caja antes de cobrar.", "warning");
      return;
    }
    if (editingPriceItemId) {
      const editingItem = saleItems.find((row) => String(row.id) === String(editingPriceItemId));
      if (editingItem) {
        const commitResult = await commitPriceChange(editingItem);
        if (commitResult === "override" || commitResult === "blocked" || commitResult === "error") {
          return;
        }
      }
    }
    if (!validateDraftPricesBeforeSend()) {
      return;
    }
    const invalidItem = saleItems.find((item) => {
      const qty = Number(item?.qty_presentation);
      return !item?.product_presentation_id || !Number.isInteger(qty) || qty <= 0;
    });
    if (invalidItem) {
      showSnack("Revisa presentaciones y cantidades (solo enteros) antes de enviar.", "warning");
      return;
    }

    if (!cashierQueueEnabled) {
      handleOpenPayDialog(false);
      return;
    }

    if (canPayNow) {
      handleOpenPayDialog(true);
      return;
    }

    await sendSaleToCash();
  };

  const handleOpenNewDraftTab = useCallback(() => {
    navigate(buildSalesDraftPath(createSalesDraftId()));
  }, [navigate]);

  const handlePayDialogCancel = async () => {
    if (paySubmitting) return;
    setPayDialogOpen(false);
    if (!payFallbackToPendingOnClose) return;
    setPayFallbackToPendingOnClose(false);
    await sendSaleToCash();
  };

  const handleConfirmPay = async (paymentPayload) => {
    if (!branchId) return;
    if (hasPendingPosCart) {
      await confirmTransferredCart("IN_CASHIER", paymentPayload);
      return;
    }
    if (!sale?.id) return;
    if (hasBlockedSaleItems) {
      showSnack(
        stockCountBlock.mode === "GENERAL"
          ? GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE
          : SELECTIVE_STOCK_COUNT_SALES_BLOCK_MESSAGE,
        "warning"
      );
      return;
    }
    if (!hasOpenCashSession) {
      showSnack("Debe abrir caja para confirmar el cobro.", "warning");
      return;
    }

    const method = String(paymentPayload?.payment_method || "CASH").toUpperCase();
    const referenceParts = [paymentPayload?.reference || ""];
    if (paymentPayload?.bank) {
      referenceParts.push(`Banco: ${paymentPayload.bank}`);
    }
    if (paymentPayload?.payment_date) {
      referenceParts.push(`Fecha: ${paymentPayload.payment_date}`);
    }
    const composedReference = referenceParts.filter(Boolean).join(" | ");
    const shouldAutoPrint = method === "CASH" || method === "CARD" || method === "VOUCHER";

    setPaySubmitting(true);
    try {
      const updated = await saleService.paySale(sale.id, {
        branch_id: branchId,
        customer_id: saleHeader.customer_id ?? null,
        payments: [
          {
            payment_method: method,
            amount: saleTotal.toFixed(2),
            cash_received: method === "CASH" ? paymentPayload?.cash_received || saleTotal.toFixed(2) : undefined,
            reference: composedReference,
            account_id: method === "TRANSFER" ? paymentPayload?.account_id : undefined,
          },
        ],
      });
      setSale(updated || sale);
      setPayFallbackToPendingOnClose(false);
      setPayDialogOpen(false);
      const nextStatus = String(updated?.status || "").toUpperCase();
      emitCashSessionChanged({ branchId, action: "sale_paid", saleId: sale.id });
      if (nextStatus === "COMPLETED" && shouldAutoPrint) {
        await handlePostSaleInvoiceFlow({
          sale: updated,
          branchName: updated?.branch_name || currentBranch?.name || saleHeader.branch_name || "-",
          cashierName: getTicketCashierName(updated),
          allowTicketPrint: shouldAutoPrint,
        });
      }
      completeSaleSubmission(
        nextStatus === "WAITING_PAYMENT"
          ? "Transferencia registrada. Venta en espera de aprobacion."
          : "Venta cobrada correctamente.",
        nextStatus === "WAITING_PAYMENT" ? "info" : "success"
      );
    } catch (error) {
      const detail = error?.response?.data?.detail || "No se pudo cobrar la venta.";
      showSnack(detail, "error");
    } finally {
      setPaySubmitting(false);
    }
  };

  if (!canManageSales) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para gestionar ventas.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <MainText icon={AddShoppingCartRounded} title={'Nueva venta'} subtitle={'Agrega productos para crear la venta'} width="50%"/>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={handleOpenNewDraftTab}>
            Nueva venta
          </Button>
          <Button variant="outlined" onClick={() => navigate("/sales/history")}>
            Historial
          </Button>
          <Button variant="outlined" onClick={() => navigate("/sales/history")}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSendToCashClick}
            disabled={primarySaleActionDisabled}
          >
            {primarySaleActionLabel}
          </Button>
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert sx={{ mb: 1.5 }} />

      {!cashierQueueEnabled && !canPayNow ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Esta sucursal no usa bandeja de cobro. Asigna el permiso "Cobrar al instante" para poder completar ventas.
        </Alert>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "380px 1fr", gap: 2 }}>
        <Card sx={{ borderLeft: `4px solid ${theme.palette.primary.main}` }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 1.25 }}>
              Datos de la venta
            </Typography>
            <Stack spacing={1.5}>
              <Stack spacing={1}>
                <TextField
                  label="Telefono del cliente"
                  size="small"
                  variant="outlined"
                    value={saleHeader.customer_phone}
                    placeholder="Opcional"
                    onChange={handleCustomerPhoneChange}
                    inputProps={{ maxLength: PHONE_MAX_LENGTH, inputMode: "numeric" }}
                    fullWidth
                />
                <TextField
                  label="Cliente asociado"
                  size="small"
                  variant="outlined"
                  value={customerDisplayValue}
                  helperText={customerLookupHelperText}
                  fullWidth
                  disabled
                />
                {customerPhoneQuery && customerLookupStatus === "not_found" ? (
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setCustomerCreateDialogOpen(true)}
                      disabled={customerLookupLoading}
                    >
                      Crear cliente
                    </Button>
                  </Stack>
                ) : null}
              </Stack>
              <TextField
                label="Fecha"
                size="small"
                type="date"
                variant="outlined"
                value={saleHeader.sale_date}
                onChange={(event) => setSaleHeader((prev) => ({ ...prev, sale_date: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField label="Sucursal" size="small" variant="outlined" value={saleHeader.branch_name} fullWidth disabled />
              <TextField
                label="Notas"
                size="small"
                variant="outlined"
                multiline
                minRows={3}
                value={saleHeader.notes}
                onChange={(event) => setSaleHeader((prev) => ({ ...prev, notes: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Ticket"
                size="small"
                variant="outlined"
                value={sale?.sale_number && sale.sale_number !== "BORRADOR" ? sale.sale_number : "Sin confirmar"}
                fullWidth
                disabled
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: `4px solid ${theme.palette.secondary?.main || theme.palette.primary.light}`, minWidth: 0 }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0 }}>
            {!hasOpenCashSession ? (
              <Alert severity="warning" variant="outlined">
                {NO_OPEN_CASH_RESERVATION_MESSAGE}
              </Alert>
            ) : null}
            {stockCountBlock.mode === "GENERAL" ? (
              <Alert severity="error" variant="outlined">
                {GENERAL_STOCK_COUNT_SALES_BLOCK_MESSAGE}
              </Alert>
            ) : null}
            {stockCountBlock.mode === "SELECTIVE" ? (
              <Alert severity="warning" variant="outlined">
                Hay productos en conteo selectivo. Puedes consultarlos, pero no venderlos ni confirmar la venta mientras sigan en conteo.
              </Alert>
            ) : null}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
              Presentaciones y precios
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <TextField
                label="Buscar producto"
                size="small"
                variant="outlined"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && searchResults.length > 0) {
                    event.preventDefault();
                    handleAddProduct(searchResults[0]);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                helperText={
                  hasOpenCashSession
                    ? "Enter agrega el primer resultado"
                    : "Sin caja abierta: puedes consultar productos, pero no vender."
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={pinSearchResults}
                    onChange={(event) => setPinSearchResults(event.target.checked)}
                  />
                }
                label="Anclar"
                sx={{ ml: { md: 0.5 } }}
              />
            </Stack>

            {searchTerm.trim().length >= 2 && (
              <Paper variant="outlined" sx={{ maxHeight: 170, overflowY: "auto", p: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">Buscando...</Typography>
                ) : searchResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Sin resultados.</Typography>
                ) : (
                  <Stack spacing={0.7}>
                    {searchResults.map((row) => (
                      (() => {
                        const stockMeta = searchStockByProduct[String(row.id)];
                        const hasStockInfo = stockMeta !== undefined;
                        const stockColor = stockMeta?.color || "default";
                        const alreadyAdded = addedProductIds.has(String(row.id));
                        const countBlockMeta = getProductCountBlockMeta(row);
                        const stockLabel = stockMeta?.label || "Sin stock";
                        return (
                          <Stack
                            key={row.id}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              p: 0.8,
                              borderRadius: 1,
                              bgcolor: alreadyAdded
                                ? alpha(theme.palette.success.main, 0.06)
                                : "background.default",
                              borderLeft: alreadyAdded
                                ? `3px solid ${theme.palette.success.main}`
                                : "3px solid transparent",
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {row.description || "Producto"}
                              </Typography>
                              {alreadyAdded ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "success.main", fontWeight: 600, fontSize: "0.7rem", display: "block" }}
                                >
                                  ✓ Ya en venta
                                </Typography>
                              ) : null}
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {row.code || "-"}
                              </Typography>
                              {hasStockInfo ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                                  {stockMeta?.presentationName || "Presentacion"} - {toMoney(stockMeta?.unit_price)}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.4 }}>
                                <Tooltip title={stockMeta?.tooltip || "Stock total: 0. Reservado: 0. Disponible: 0."}>
                                  <Chip
                                    size="small"
                                    color={stockColor}
                                    variant="outlined"
                                    label={hasStockInfo ? stockLabel : "..."}
                                  />
                                </Tooltip>
                                {countBlockMeta.blocked ? (
                                  <Tooltip title={countBlockMeta.detail}>
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="filled"
                                      label={countBlockMeta.label}
                                    />
                                  </Tooltip>
                                ) : null}
                              </Stack>
                            </Box>
                            <Button
                              size="small"
                              variant={countBlockMeta.blocked ? "text" : "outlined"}
                              color={countBlockMeta.blocked ? "warning" : alreadyAdded ? "success" : "primary"}
                              startIcon={<AddShoppingCartOutlined fontSize="small" />}
                              onClick={() => handleAddProduct(row)}
                              disabled={
                                itemUpdating ||
                                countBlockMeta.blocked ||
                                !hasStockInfo ||
                                !stockMeta?.canFulfillRequested
                              }
                            >
                              {countBlockMeta.blocked
                                ? "No disponible"
                                : alreadyAdded
                                  ? "Agregar mas"
                                  : "Agregar"}
                            </Button>
                          </Stack>
                        );
                      })()
                    ))}
                  </Stack>
                )}
              </Paper>
            )}

            <Paper variant="outlined" sx={{ minHeight: 260, maxHeight: "calc(100vh - 370px)", overflow: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Presentacion</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="center">Disponible</TableCell>
                    <TableCell align="right">Precio unitario</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">Accion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleSaleItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary">Agrega productos para iniciar la venta.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleSaleItems.map((item) => {
                      const rowId = String(item.id);
                      const qtyValue = qtyDraftByItem[rowId] ?? String(parseInt(item.qty_presentation || 0, 10));
                      const priceValue = priceDraftByItem[rowId] ?? Number(item.unit_price || 0).toFixed(2);
                      const { options } = getItemPricingContext(item);
                      const countBlockMeta = getItemCountBlockMeta(item);
                      const stockChip = countBlockMeta.blocked
                        ? { color: "warning", label: countBlockMeta.label }
                        : getStockChipState(item);
                      const rowStockError = stockLineErrors[rowId];
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{item.product_description}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              disabled={countBlockMeta.blocked || itemUpdating}
                              value={item.product_presentation_id || ""}
                              onChange={(event) => handlePresentationChange(item, Number(event.target.value))}
                              sx={{ minWidth: 170 }}
                            >
                              {options.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  {option.name} ({Number(option.qty_in_base || 0).toFixed(3)})
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              variant="outlined"
                              type="number"
                              disabled={countBlockMeta.blocked || itemUpdating}
                              value={qtyValue}
                              error={Boolean(rowStockError)}
                              helperText={rowStockError?.message || " "}
                              onChange={(event) => {
                                const sanitized = sanitizeIntegerInput(event.target.value);
                                const cachedMaxUnits = getCachedMaxUnitsForItem(item);
                                const nextValue =
                                  sanitized && Number.isInteger(cachedMaxUnits)
                                    ? cachedMaxUnits > 0
                                      ? String(Math.min(Number(sanitized), cachedMaxUnits))
                                      : sanitized
                                    : sanitized;
                                setQtyDraftByItem((prev) => ({
                                  ...prev,
                                  [rowId]: nextValue,
                                }));
                              }}
                              onBlur={() => commitQtyChange(item)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  commitQtyChange(item);
                                }
                              }}
                              inputProps={{
                                min: 1,
                                step: "1",
                                max: Number.isInteger(getCachedMaxUnitsForItem(item)) &&
                                  getCachedMaxUnitsForItem(item) > 0
                                  ? getCachedMaxUnitsForItem(item)
                                  : undefined,
                              }}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={getStockTooltipContent(item, countBlockMeta)} arrow placement="top">
                              <Box component="span">
                                <Chip size="small" color={stockChip.color} label={stockChip.label} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            {editingPriceItemId === rowId ? (
                              <TextField
                                size="small"
                                variant="outlined"
                                type="number"
                                disabled={countBlockMeta.blocked || itemUpdating}
                                value={priceValue}
                                autoFocus
                                onFocus={(event) => event.target.select()}
                                onChange={(event) => handlePriceDraftChange(item, event.target.value)}
                                onBlur={() => commitPriceChange(item)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    commitPriceChange(item);
                                  }
                                }}
                                inputProps={{ step: "0.01" }}
                                sx={{ width: 120 }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  cursor: countBlockMeta.blocked ? "default" : "pointer",
                                  display: "inline-block",
                                  px: 0.5,
                                  color: countBlockMeta.blocked ? "text.secondary" : "text.primary",
                                }}
                                onClick={() => {
                                  if (countBlockMeta.blocked) return;
                                  handleStartPriceEdit(item);
                                }}
                              >
                                {toMoney(item.unit_price)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{toMoney(item.line_total)}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(item)} disabled={itemUpdating}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Divider />

            <Stack spacing={0.8}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body2">{toMoney(saleSubtotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Descuento</Typography>
                <Typography variant="body2">{toMoney(saleDiscountTotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Impuestos</Typography>
                <Typography variant="body2">{toMoney(saleTaxTotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>Total</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>{toMoney(saleTotal)}</Typography>
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="contained"
                onClick={handleSendToCashClick}
                disabled={primarySaleActionDisabled}
              >
                {primarySaleActionLabel}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <SalePaymentDialog
        open={payDialogOpen}
        onCancel={handlePayDialogCancel}
        onConfirm={handleConfirmPay}
        total={saleTotal}
        itemsCount={visibleSaleItems.length}
        hasOpenCashSession={hasOpenCashSession}
        transferAccounts={transferAccounts}
        loadingTransferAccounts={loadingTransferAccounts}
        submitting={paySubmitting}
      />

      <PriceOverrideDialog
        open={priceOverrideDialog.open}
        data={priceOverrideDialog}
        onCancel={() => {
          const itemId = priceOverrideDialog?.item?.id;
          if (itemId) {
            const basePrice = Number(priceOverrideDialog?.basePrice || 0).toFixed(2);
            setPriceDraftByItem((prev) => ({ ...prev, [String(itemId)]: basePrice }));
          }
          setPriceOverrideDialog({
            open: false,
            item: null,
            newUnitPrice: null,
            basePrice: null,
            costPrice: null,
          });
          setEditingPriceItemId(null);
        }}
        onConfirm={handleConfirmPriceOverride}
        submitting={itemUpdating}
      />

      <CustomerQuickCreateDialog
        open={customerCreateDialogOpen}
        phoneNumber={saleHeader.customer_phone}
        onClose={() => setCustomerCreateDialogOpen(false)}
        onCreated={(customer) => handleCustomerSelected(customer, "Cliente creado y asociado.")}
      />

      {invoiceDialog}
      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default SalesPage;
